/**
 * spacetime_grid_controller.js
 *
 * Decorative "curved space-time" grid for the landing roadmap section. A
 * canvas is injected behind the section's content and a line grid is drawn
 * as a heightfield: a Gaussian hill rises under the pointer. Each vertex is
 * lifted by the hill's height, pushed outward by its slope (the mesh stretches
 * over the bump rather than gathering into it), and each segment is shaded
 * from its surface normal against a light from the top-left, so the mound has
 * a lit face and a shadowed face and reads as 3D. The pointer is applied
 * instantly; only the hill's height fades when the pointer leaves. The grid is
 * drawn well past the section's edges so a lifted vertex near the boundary
 * never exposes an edge.
 *
 * Pointer-events are disabled on the canvas so it never interferes with the
 * marquee lanes above it. Under prefers-reduced-motion a flat static grid is
 * drawn once and no animation loop runs.
 *
 * Wiring (templates/landing.html):
 *   <section class="shed" id="roadmap" data-controller="spacetime-grid">
 * Styling: static/css/landing.css (.spacetime-canvas, .shed positioning).
 */

import { Controller } from "@hotwired/stimulus";

const SPACING = 44;        // grid cell size in CSS px
const RADIUS = 95;         // sigma of the Gaussian hill in CSS px
const LIFT = 52;           // peak height of the hill in CSS px
const SPREAD = 0.45;       // how far the mesh stretches outward over the bump (per unit slope)
const TILT = 0.85;         // how much of the height shows as upward screen movement (view angle)
const FADE = 0.18;         // amplitude fade per frame after the pointer leaves
const OVERSCAN = 4;        // extra grid cells drawn past each edge
const LIGHT = norm3(-0.55, -0.6, 0.58); // light from the top-left, above the sheet

function norm3(x, y, z) { const l = Math.hypot(x, y, z); return { x: x / l, y: y / l, z: z / l }; }

export default class extends Controller {
    connect() {
        this.canvas = document.createElement('canvas');
        this.canvas.className = 'spacetime-canvas';
        this.canvas.setAttribute('aria-hidden', 'true');
        this.element.prepend(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.pointer = { x: -1e4, y: -1e4 }; // last known pointer position (section coords)
        this.inside = false;                 // pointer currently over the section
        this.amp = 0;                        // hill amplitude, 1 while inside, fades to 0 after leaving
        this.raf = null;

        this._onMove = (e) => this._track(e);
        this._onLeave = () => { this.inside = false; this._wake(); };
        this._onResize = () => { this._resize(); this._wake(); };

        this._resize();
        if (!this.reduced) {
            this.element.addEventListener('pointermove', this._onMove);
            this.element.addEventListener('pointerleave', this._onLeave);
            this.resizeObserver = new ResizeObserver(this._onResize);
            this.resizeObserver.observe(this.element);
        }
        this._draw();
    }

    disconnect() {
        this.element.removeEventListener('pointermove', this._onMove);
        this.element.removeEventListener('pointerleave', this._onLeave);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
        if (this.canvas && this.canvas.isConnected) this.canvas.remove();
    }

    _track(e) {
        const r = this.element.getBoundingClientRect();
        this.pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
        this.inside = true;
        this.amp = 1;
        this._wake();
    }

    _wake() {
        if (!this.raf) this.raf = requestAnimationFrame(() => this._frame());
    }

    _resize() {
        const dpr = window.devicePixelRatio || 1;
        this.w = this.element.clientWidth;
        this.h = this.element.clientHeight;
        this.canvas.width = Math.round(this.w * dpr);
        this.canvas.height = Math.round(this.h * dpr);
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    _frame() {
        this.raf = null;
        if (!this.inside) this.amp = this.amp < 0.01 ? 0 : this.amp * (1 - FADE);
        this._draw();
        if (this.inside || this.amp > 0) this._wake();
    }

    /**
     * Project a grid vertex through the hill heightfield.
     * Returns the screen point plus `g` (0..1 hill weight) and `shade`
     * (surface-normal lighting, 0..1) for the line renderer.
     */
    _warp(x, y) {
        const rx = x - this.pointer.x;          // vertex relative to the peak
        const ry = y - this.pointer.y;
        const s2 = RADIUS * RADIUS;
        const g = Math.exp(-(rx * rx + ry * ry) / (2 * s2)) * this.amp;
        const z = LIFT * g;
        // Slope of the heightfield (dz/dx, dz/dy).
        const zx = -z * rx / s2;
        const zy = -z * ry / s2;
        // Surface normal against the light: flat sheet = LIGHT.z, lit face > that, shadow face < that.
        const n = norm3(-zx, -zy, 1);
        const shade = n.x * LIGHT.x + n.y * LIGHT.y + n.z * LIGHT.z;
        // Stretch outward along the slope, then lift toward the viewer.
        return {
            x: x - zx * SPREAD * RADIUS,
            y: y - zy * SPREAD * RADIUS - z * TILT,
            g,
            shade,
        };
    }

    _draw() {
        const ctx = this.ctx;
        const c0 = -OVERSCAN, c1 = Math.ceil(this.w / SPACING) + OVERSCAN;
        const r0 = -OVERSCAN, r1 = Math.ceil(this.h / SPACING) + OVERSCAN;
        ctx.clearRect(0, 0, this.w, this.h);
        ctx.lineWidth = 1;

        // Flat sheet alpha 0.08; lit slopes brighten toward 0.5, shadowed slopes dim toward 0.03.
        const flat = LIGHT.z;
        const stroke = (p) => {
            const lit = Math.max(-1, Math.min(1, (p.shade - flat) * 2.2));
            const a = lit >= 0 ? 0.08 + 0.42 * lit : 0.08 + 0.05 * lit;
            return `rgba(232, 228, 218, ${a.toFixed(3)})`;
        };

        for (let r = r0; r <= r1; r++) {
            this._polyline(c0, c1, (c) => this._warp(c * SPACING, r * SPACING), stroke);
        }
        for (let c = c0; c <= c1; c++) {
            this._polyline(r0, r1, (r) => this._warp(c * SPACING, r * SPACING), stroke);
        }
    }

    /** Draw one grid line segment-by-segment so shading can follow the surface. */
    _polyline(i0, i1, at, stroke) {
        const ctx = this.ctx;
        let prev = at(i0);
        for (let i = i0 + 1; i <= i1; i++) {
            const p = at(i);
            ctx.strokeStyle = stroke(p.g > prev.g ? p : prev);
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            prev = p;
        }
    }
}
